import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";
import { setupPushNotifications } from "@/lib/pushNotifications";
import { initTelemetry } from "@/lib/telemetry";
import { Capacitor } from '@capacitor/core';
import { logger } from "@/lib/logger";
import { unifiedLogger } from "@/lib/observability/unified-logger";
import { initNetworkLogger } from "@/lib/observability/network-logger";
import { persistentLogger } from "@/lib/persistent-logger";

// Inicializar Observability Stack
if (import.meta.env.VITE_OTEL_ENABLED !== 'false') {
  initTelemetry();
}
initNetworkLogger();
unifiedLogger.info('App initialized', 'app.startup', {});

// ✅ Log inicial no persistent logger
persistentLogger.addLog('APP', 'info', 'App initialized', {
  version: '1.0.0',
  platform: Capacitor.getPlatform(),
  isNative: Capacitor.isNativePlatform()
});

// Inicializar Sentry para crash reporting
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
  });
  
  // Expor Sentry globalmente para uso nos services
  (window as any).Sentry = Sentry;
}

// Limpar Service Workers e caches em ambientes não-produção
if (!import.meta.env.PROD && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => registration.unregister());
  });
  
  if ('caches' in window) {
    caches.keys().then(cacheNames => {
      cacheNames.forEach(cacheName => caches.delete(cacheName));
    });
  }
}

// Inicializar push notifications em plataformas nativas
if (Capacitor.isNativePlatform()) {
  setupPushNotifications().catch(err => {
    logger.error('Failed to setup push notifications', err, {
      component: 'main',
      platform: Capacitor.getPlatform()
    });
    Sentry.captureException(err);
  });
}

import { ErrorBoundary } from '@/components/ErrorBoundary';

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
